import { msg, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { TFPageContent } from '@tf/shared';
import { Box, Button, Icon, Stack, TFCard, TFText, useDisclosure } from '@tf/ui';
import type React from 'react';
import { useEffect } from 'react';
import { AddApplicationModal } from '@/components/application/AddApplicationModal';
import { ApplicationsList } from '@/components/application/ApplicationsList';
import { MarkdownPageIntro, useMenu } from '@/components/shared';
import { useApplicationsList } from '@/core/api';
import { useDefinitions } from '@/core/providers';

import { usePageTitle } from '../../usePageTitle';

const defaultTermsText = `
# Welcome. Please select an account type.

Select an account type you’d like to open and follow the steps to complete your application.
`;

export const MainRoute: React.FC = () => {
	const { _ } = useLingui();
	const { defs } = useDefinitions();

	const newApplicationModal = useDisclosure();
	const applicationsList = useApplicationsList();
	const applications = applicationsList.data ?? [];
	const hasApplications = applications.length > 0;
	const isLoading = applicationsList.isPending;
	const shouldLimitWidth = !isLoading && !hasApplications;

	usePageTitle(
		_(
			msg({
				id: 'mainPage.title',
				message: 'New application',
			}),
		),
	);

	const { setMenuItems } = useMenu();
	useEffect(() => {
		setMenuItems([]);
	}, [setMenuItems]);

	return (
		<TFPageContent m="0">
			<Stack align="center" mb="24px">
				<MarkdownPageIntro text={defs.customization.terms_text ?? defaultTermsText} />
			</Stack>
			<TFCard
				innerPadding="36px 48px 36px"
				maw={shouldLimitWidth ? 500 : undefined}
				mx={shouldLimitWidth ? 'auto' : undefined}
			>
				<Stack gap="16px">
					{hasApplications && (
						<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'end' }}>
							<TFText size="lg">Applications</TFText>
							<Button
								onClick={newApplicationModal.open}
								leftSection={<Icon.IconFilePlus size={16} />}
								sx={{
									'& .mantine-Button-section': {
										marginRight: '8px',
									},
								}}
							>
								<Trans id="mainPage.startApplicationButton">Start new application</Trans>
							</Button>
						</Box>
					)}
					<ApplicationsList />
				</Stack>
			</TFCard>

			<AddApplicationModal
				title={_(msg({ id: 'mainPage.startApplicationButton', message: 'Start new application' }))}
				opened={newApplicationModal.isOpen}
				onClose={newApplicationModal.close}
			/>
		</TFPageContent>
	);
};
